import React, { createContext, useState, useMemo, useContext, useEffect, useRef } from "react"
import Api from '../_helpers/Api'
import moment from "moment"
import { setSentryUser } from '../_config/sentry'

export const AuthDataContext = createContext({})

const initialAuthData = {
    isLogged: false,
    data: {},
    booted: false,
}

// на колко време се обновява токенът (и минимален интервал между заявките за обновяване)
const REFRESH_INTERVAL = 30 * 1000

// автоматичен изход при неактивност
const INACTIVITY_TIMEOUT = 5 * 60 * 1000

// на колко време се проверява за неактивност
const INACTIVITY_CHECK_INTERVAL = 5 * 1000

// най-често на толкова се записва последната активност в localStorage (за другите табове)
const ACTIVITY_STORAGE_THROTTLE = 5 * 1000

const LAST_ACTIVITY_KEY = 'last-activity'

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'click', 'wheel', 'scroll', 'keydown', 'touchstart']

// let interval
let inactivityInterval

const AuthDataProvider = props => {
    const [authData, setAuthData] = useState(initialAuthData)
    const [neccesarySettings, setNeccesarySettings] = useState([])
    const [booted, setBooted] = useState(false)

    const lastActivityRef = useRef(new Date().getTime())
    const lastActivityStoredRef = useRef(0)
    const loggingOutRef = useRef(false)

    const lastRefreshAtRef = useRef(0)
    const refreshInProgressRef = useRef(false)

    const getAuthData = () => {
        let currentAuthData = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')) : initialAuthData

        setAuthData(currentAuthData)
        setBooted(true)

        // if (currentAuthData.isLogged) {
        //     refresh();
        // }

    }

    const loadData = async (retry = 0) => {
        return await Api.get('profile')
            .then(response => {

                let state = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')) : initialAuthData
                let profile = response.data

                if (profile.is_employee) {
                    if (!profile.active || !profile.partner.active) {
                        return logout()
                    }
                }

                if (state && state.data && state.data.user) {
                    state.data.user = profile
                }

                setAuthData(state)

                localStorage.setItem('auth', JSON.stringify(state))

                return true

            }).catch(err => {

                if (retry > 2) {
                    return logout()
                } else {
                    return loadData(retry + 1)
                }

            })
    }

    const refresh = async () => {
        loadData()
    }

    const login = (newAuthData) => {

        let data = Object.assign({}, newAuthData)

        let expiresIn = data.expires_in

        if (expiresIn > 0) {
            let expiresAt = new Date(new Date().getTime() + (expiresIn * 1000))
            let expiresAtTimestamp = expiresAt.getTime()

            data.expires_at = expiresAt
            data.expires_at_timestamp = expiresAtTimestamp
        }

        let state = {
            isLogged: true,
            data: data
        }

        localStorage.setItem('auth', JSON.stringify(state))

        setAuthData(state)
    }

    const refreshToken = () => {
        const now = Date.now()

        if (refreshInProgressRef.current) {
            return
        }

        if (now - lastRefreshAtRef.current < REFRESH_INTERVAL) {
            return
        }

        lastRefreshAtRef.current = now
        refreshInProgressRef.current = true

        Api.post('refresh')
            .then(response => {
                login(response.data)
            })
            .catch(error => {
                // logout();
            })
            .finally(() => {
                refreshInProgressRef.current = false
            })
    }

    const logout = async (redirect = true) => {
        return await Api.post('logout')
            .finally(() => {
                // setAuthData(initialAuthData);
                localStorage.clear()

                if (redirect) {
                    window.location = '/'
                }
            })
    }

    const setSettings = data => {
        if (authData.data && authData.data.user) {
            let newData = Object.assign({}, authData.data)

            newData.user.settings = data

            login(newData)
        }
    }

    const loadNeccesarySettings = async (bool = true) => {
        return await Api.get('partners/settings/neccesary')
            .then(res => {
                setNeccesarySettings(res.data)

                if (bool) {
                    return true
                }

                return res.data
            })
    }

    const user = () => {
        if (authData.data && authData.data.user) {
            return authData.data.user
        }
        return null
    }

    const id = () => {
        if (user()) {
            return user().id
        }
        return null
    }

    const type = () => {
        if (user()) {
            return user().type
        }
        return null
    }

    const token = () => {
        if (authData.data) {
            return authData.data.access_token
        }
        return null
    }

    const partner = () => {
        if (authData.data && authData.data.user && authData.data.user.partner) {
            return authData.data.user.partner
        }
        return null
    }

    const partnerId = () => {
        if (authData.data && authData.data.user) {
            return authData.data.user.partner_id
        }
        return null
    }

    const setting = key => {
        if (user() && user().settings) {
            return user().settings[key] || null
        }

        return null
    }

    const lang = () => {
        if (setting('language')) {
            return setting('language').code
        }

        return null
    }

    const langId = () => {
        if (setting('language')) {
            return setting('language').id
        }

        return null
    }

    const phoneCode = () => {
        if (setting('language')) {
            return setting('language').phone_code
        }

        return null
    }

    // съществува ли в плана
    const module = key => {
        if (user() && user().modules && Array.isArray(user().modules)) {
            return user().modules.includes(key)
        }

        return false
    }

    function getSubscription() {
        const exporesAtFormatterd = user()?.partner?.license_expires_at_dmy


        return {
            expiresAtFormatted: exporesAtFormatterd,
            isExpired: !!moment(exporesAtFormatterd, 'DD.MM.YYYY').isBefore(moment()),
            expiresSoon: !!moment(exporesAtFormatterd, 'DD.MM.YYYY').isBefore(moment().add(7, 'days'))
        }
    }

    //този user има ли достъп
    const access = key => {

        if (isSuperAdmin()) {
            return true
        }

        if (user() && user().access && typeof user().access === 'object') {
            return user().access[key] || false
        }

        return false
    }

    // Проверява дали потребителят има достъп и модула има ли го в плана
    const permission = key => {
        return module(key) && access(key)
    }

    const isAdmin = () => {
        if (user()) {
            return Boolean(user().is_administrator)
        }
        //return false;
    }

    const isSuperAdmin = () => {
        if (user()) {
            return Boolean(user().is_administrator && user().superadmin)
        }
        return false
    }

    const isEmployee = () => {
        if (user()) {
            return Boolean(user().is_employee)
        }
        return false
    }

    const isManager = () => {
        if (user() && user().role) {
            return user().role.name === 'manager'
        }
        return false
    }

    const isOperator = () => {
        if (user() && user().role) {
            return user().role.name === 'operator'
        }
        return false
    }

    // записва последната активност (споделя се с останалите табове през localStorage)
    const setLastActivity = (timestamp = new Date().getTime()) => {
        lastActivityRef.current = timestamp

        if (timestamp - lastActivityStoredRef.current >= ACTIVITY_STORAGE_THROTTLE) {
            lastActivityStoredRef.current = timestamp
            localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp))
        }
    }

    // клавиатура/мишка - брои се само когато табът е на фокус
    const onActivity = () => {
        if (document.hidden || !document.hasFocus()) {
            return
        }

        setLastActivity()
    }

    // изход, ако е изтекло времето без активност
    const checkInactivity = () => {
        if (loggingOutRef.current) {
            return
        }

        if (new Date().getTime() - lastActivityRef.current < INACTIVITY_TIMEOUT) {
            return
        }

        loggingOutRef.current = true

        clearInterval(inactivityInterval)
        // clearInterval(interval)

        logout()
    }

    const onStorageUpdate = e => {
        const { key, newValue } = e

        if (key === null) {
            logout()
        }

        if (key === LAST_ACTIVITY_KEY && newValue) {
            lastActivityRef.current = Number(newValue)
        }

        if (key === 'auth') {
            // console.log(JSON.parse(newValue))

            setAuthData(JSON.parse(newValue))

            // window.location.reload();
        }
    }

    useMemo(() => {
        getAuthData()
    }, [])

    // useMemo(() => {
    //     clearInterval(interval)

    //     if (authData.isLogged) {
    //         interval = setInterval(() => {
    //             refreshToken()
    //         }, REFRESH_INTERVAL)
    //     }

    //     return () => {
    //         clearInterval(interval)
    //     }
    // }, [authData])

    useEffect(() => {
        clearInterval(inactivityInterval)

        if (!authData.isLogged) {
            return
        }

        setLastActivity()

        ACTIVITY_EVENTS.forEach(event => {
            document.addEventListener(event, onActivity, { capture: true, passive: true })
        })

        // при връщане към таба се проверява веднага дали времето вече е изтекло
        window.addEventListener('focus', checkInactivity)
        document.addEventListener('visibilitychange', checkInactivity)

        inactivityInterval = setInterval(checkInactivity, INACTIVITY_CHECK_INTERVAL)

        return () => {
            clearInterval(inactivityInterval)

            ACTIVITY_EVENTS.forEach(event => {
                document.removeEventListener(event, onActivity, { capture: true })
            })

            window.removeEventListener('focus', checkInactivity)
            document.removeEventListener('visibilitychange', checkInactivity)
        }
    }, [authData.isLogged])

    const sentryUser = authData.isLogged ? authData.data?.user : null

    useEffect(() => {
        setSentryUser(sentryUser)
    }, [sentryUser?.id, sentryUser?.partner_id])

    useEffect(() => {
        window.addEventListener("storage", onStorageUpdate)
        return () => {
            window.removeEventListener("storage", onStorageUpdate)
        }
    }, [])

    const auth = useMemo(() => ({
        ...authData,
        user,
        id,
        type,
        token,
        partner,
        partnerId,
        setting,
        setSettings,
        neccesarySettings,
        loadNeccesarySettings,
        lang,
        langId,
        phoneCode,
        access,
        module,
        permission,
        isAdmin,
        isSuperAdmin,
        isEmployee,
        isManager,
        isOperator,
        login,
        loadData,
        refresh,
        logout,
        getSubscription
    }), [authData, neccesarySettings])

    if (booted) {
        return <AuthDataContext.Provider value={auth} {...props} />
    }

    return null
}

export const useAuthDataContext = () => useContext(AuthDataContext)

export default AuthDataProvider